import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Activity, Loader2, TrendingUp, AlertTriangle, Power, DollarSign, ExternalLink, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 30 * 1000; // 30s

const SOURCE_LABELS = {
  rss_auto: "📰 RSS auto",
  rss_approved: "📰 RSS approved",
  breaking_news: "🚨 Breaking",
  auto_publish: "🤖 Auto-publish",
  support_article: "📄 Support",
  affiliate_article: "💰 Affiliate",
  mass_generated: "⚡ Mass",
  unknown: "❓ Unknown",
};

function timeAgo(iso) {
  if (!iso) return "?";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s temu`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min temu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h temu`;
  return `${Math.floor(diff / 86400)}d temu`;
}

export default function LiveDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard/live`);
      setData(res.data);
      setLastUpdate(new Date());
    } catch (_) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  if (loading || !data) return <div className="p-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const hookCov = (n) => Math.round(100 * n / Math.max(data.publications.last_24h, 1));

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Activity className="w-8 h-8 text-emerald-600" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Live Operations Dashboard</h1>
            <p className="text-sm text-muted-foreground">
              Real-time. Odświeża się co 30s · Ostatnia aktualizacja: {lastUpdate ? lastUpdate.toLocaleTimeString("pl-PL") : "—"}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-700 uppercase flex items-center gap-1">
              <Zap className="w-3 h-3" /> Publikacji 1h
            </p>
            <p className="text-3xl font-bold text-emerald-900">{data.publications.last_1h}</p>
            <p className="text-xs text-emerald-600">{data.publications.last_24h} / 24h</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-blue-700 uppercase">Publikacji 7d</p>
            <p className="text-3xl font-bold text-blue-900">{data.publications.last_7d.toLocaleString()}</p>
            <p className="text-xs text-blue-600">{data.publications.total.toLocaleString()} total</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-purple-700 uppercase flex items-center gap-1">
              <Power className="w-3 h-3" /> Aktywnych portali
            </p>
            <p className="text-3xl font-bold text-purple-900">{data.portals.active}</p>
            <p className="text-xs text-purple-600">{data.portals.stand_by} stand-by · {data.portals.total} total</p>
          </CardContent>
        </Card>
        <Card className={`border-${data.alerts.critical_open > 0 ? "red" : "slate"}-200 bg-${data.alerts.critical_open > 0 ? "red" : "slate"}-50/50`}>
          <CardContent className="p-4">
            <p className="text-xs uppercase flex items-center gap-1 text-red-700">
              <AlertTriangle className="w-3 h-3" /> Alerty krytyczne
            </p>
            <p className={`text-3xl font-bold ${data.alerts.critical_open > 0 ? "text-red-900" : "text-slate-700"}`}>{data.alerts.critical_open}</p>
            <p className="text-xs text-red-600">
              {data.alerts.service_outage_open} awarii · {data.alerts.publication_failed_open} fail publikacji
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            Hooki coverage (ostatnie 24h)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span>📌 Pillar Links</span>
                <span className="font-bold text-emerald-700">{data.hooks_coverage_24h.with_pillar_links} ({hookCov(data.hooks_coverage_24h.with_pillar_links)}%)</span>
              </div>
              <div className="w-full bg-emerald-100 rounded-full h-2">
                <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${hookCov(data.hooks_coverage_24h.with_pillar_links)}%` }} />
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span>🔗 Cross-Portal CTA</span>
                <span className="font-bold text-amber-700">{data.hooks_coverage_24h.with_cross_portal_cta} ({hookCov(data.hooks_coverage_24h.with_cross_portal_cta)}%)</span>
              </div>
              <div className="w-full bg-amber-100 rounded-full h-2">
                <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${hookCov(data.hooks_coverage_24h.with_cross_portal_cta)}%` }} />
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span>📊 Schema.org</span>
                <span className="font-bold text-purple-700">{data.hooks_coverage_24h.with_schema_markup} ({hookCov(data.hooks_coverage_24h.with_schema_markup)}%)</span>
              </div>
              <div className="w-full bg-purple-100 rounded-full h-2">
                <div className="bg-purple-500 h-2 rounded-full transition-all" style={{ width: `${hookCov(data.hooks_coverage_24h.with_schema_markup)}%` }} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Zap className="w-4 h-4 text-emerald-600" />
              Live ticker — ostatnie publikacje
              <span className="text-xs font-normal text-slate-500 ml-auto">{data.recent_publications.length} w ostatniej godzinie</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[400px] overflow-y-auto divide-y">
              {data.recent_publications.map((p, i) => (
                <div key={i} className="px-4 py-2 hover:bg-emerald-50/40 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-slate-400 whitespace-nowrap">{timeAgo(p.published_at)}</span>
                    <Badge variant="outline" className="text-[10px]">{p.site_name}</Badge>
                    {p.source_type && p.source_type !== "auto_publish" && (
                      <Badge variant="outline" className="text-[10px]">{SOURCE_LABELS[p.source_type] || p.source_type}</Badge>
                    )}
                    {p.money_score > 0 && (
                      <Badge className="text-[10px] bg-amber-100 text-amber-800">💰 {p.money_score}</Badge>
                    )}
                    {p.pillar_links_added > 0 && <span className="text-xs text-emerald-600" title="Pillar links">📌</span>}
                    {p.cross_portal_cta_added > 0 && <span className="text-xs text-amber-600" title="Cross-portal CTA">🔗</span>}
                  </div>
                  <div className="text-sm mt-0.5 truncate">
                    {p.published_url ? (
                      <a href={p.published_url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                        {p.title}
                      </a>
                    ) : p.title}
                  </div>
                </div>
              ))}
              {data.recent_publications.length === 0 && (
                <div className="p-6 text-center text-sm text-slate-500">Brak publikacji w ostatniej godzinie</div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">🏆 TOP 10 portali / 24h</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {data.top_portals_24h.map((p, i) => (
                  <div key={p.site_id} className="px-4 py-2 flex items-center gap-3 text-sm hover:bg-slate-50">
                    <span className="text-xs text-slate-400 w-6">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{p.name}</span>
                        {p.auto_publish ? (
                          <Badge className="text-[10px] bg-emerald-100 text-emerald-800">aktywny</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">stand-by</Badge>
                        )}
                      </div>
                    </div>
                    <div className="font-bold text-emerald-700">{p.posts_24h}</div>
                    {p.url && (
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">📦 Rozkład źródeł / 24h</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(data.source_breakdown_24h)
                  .sort((a, b) => b[1] - a[1])
                  .map(([source, count]) => {
                    const pct = Math.round(100 * count / Math.max(data.publications.last_24h, 1));
                    return (
                      <div key={source} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{SOURCE_LABELS[source] || source}</span>
                          <span className="font-bold">{count} ({pct}%)</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5">
                          <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">💰 Content stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-slate-500 uppercase">Money posts</div>
                  <div className="text-2xl font-bold text-emerald-700">{data.content_stats.money_posts_total.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase">Pillarów</div>
                  <div className="text-2xl font-bold text-purple-700">{data.content_stats.pillars_total.toLocaleString()}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
